import React, { useState, useEffect } from 'react';
import AnnonceCard from '../../elements/annonce_card';
import AnnonceList from '../../elements/annonce_list';
import { Plus, Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BASE_URL } from '../../apiConfig';
import { toast } from 'sonner';
import Loading from '../../cors/Loading';
import { useAuth } from "../../context/AuthProvider";

export default function AnnoncesPage() {
  const { token, role, roles } = useAuth();
  const [annonces, setAnnonces] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("date_start");
  const [filterBy, setFilterBy] = useState("all");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnnonces = async () => {
    setError(null);
    try {
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await fetch(`${BASE_URL}/api/annonces`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors du chargement des annonces');
      }

      const data = await response.json();
      
      // Handle different response formats
      if (data.data) {
        setAnnonces(data.data);
      } else if (Array.isArray(data)) {
        setAnnonces(data);
      } else if (data.annonces) {
        setAnnonces(data.annonces);
      } else {
        setAnnonces([]);
      }
    } catch (err) {
      console.error('Error fetching annonces:', err);
      setError(err.message);
      toast.error(`Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnonces();
  }, []);

  const getAnnonceStatus = (annonce) => {
    const now = new Date();
    const start = new Date(annonce.date_start);
    const end = new Date(annonce.date_end);
    
    if (now >= start && now <= end) return 'active';
    if (now < start) return 'upcoming';
    return 'expired';
  };

  const filteredAnnonces = annonces
    .filter((annonce) => {
      const matchesSearch = annonce.annonce.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;

      if (filterBy === 'all') return true;
      return getAnnonceStatus(annonce) === filterBy;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "date_start":
          return new Date(b.date_start).getTime() - new Date(a.date_start).getTime();
        case "date_end":
          return new Date(b.date_end).getTime() - new Date(a.date_end).getTime();
        case "created_at":
          return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
        case "content":
          return a.annonce.localeCompare(b.annonce);
        default:
          return 0;
      }
    });

  const handleEdit = (annonce) => {
    console.log('Editing annonce:', annonce);
    // Implement edit functionality - redirect to edit page
    window.location.href = `/annonces/edit/${annonce.id}`;
  };

  const getStatusCounts = () => {
    const active = annonces.filter(a => getAnnonceStatus(a) === 'active').length;
    const upcoming = annonces.filter(a => getAnnonceStatus(a) === 'upcoming').length;
    const expired = annonces.filter(a => getAnnonceStatus(a) === 'expired').length;
    return { active, upcoming, expired };
  };

  const statusCounts = getStatusCounts();

  // Filter buttons component
  const FilterButtons = () => (
    <div className="flex flex-wrap gap-2 mb-6">
      <button
        onClick={() => setFilterBy('all')}
        className={`px-4 py-2 cursor-pointer rounded-xl text-xs font-medium transition-all duration-200 ${
          filterBy === 'all'
            ? 'bg-[var(--color-primary-500)] text-white shadow-lg'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        Toutes ({annonces.length})
      </button>
      <button
        onClick={() => setFilterBy('active')}
        className={`px-4 py-2 cursor-pointer rounded-xl text-xs font-medium transition-all duration-200 ${
          filterBy === 'active'
            ? 'bg-green-500 text-white shadow-lg'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        Actives ({statusCounts.active})
      </button>
      <button
        onClick={() => setFilterBy('upcoming')}
        className={`px-4 py-2 cursor-pointer rounded-xl text-xs font-medium transition-all duration-200 ${
          filterBy === 'upcoming'
            ? 'bg-amber-500 text-white shadow-lg'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        À venir ({statusCounts.upcoming})
      </button>
      <button
        onClick={() => setFilterBy('expired')}
        className={`px-4 py-2 cursor-pointer rounded-xl text-xs font-medium transition-all duration-200 ${
          filterBy === 'expired'
            ? 'bg-red-500 text-white shadow-lg'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        Expirées ({statusCounts.expired})
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-5">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              Annonces
            </h1>
            <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
              Gérez vos annonces et communications avec vos clients.
            </p>
          </div>
          {
            (role === 1 || (role === 2 && roles.add_annonce === 1)) &&
              <div className="mt-6 md:mt-0 flex gap-3">
                <Link to="/annonces/create">
                  <button className="cursor-pointer text-xs max-xl:py-3 bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white font-semibold max-xl:py-2 max-xl:px-4 py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                    <span className="flex items-center">
                      <Plus className="h-4 w-4 mr-2" />
                      Nouvelle Annonce
                    </span>
                  </button>
                </Link>
              </div>
          }
        </div>

        {/* Search and Controls */}
         <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-4 py-2 max-xl:p-4 mb-8">
            <div className="flex flex-col items-center xl:flex-row gap-6 max-xl:gap-4">
              {/* Search Bar */}
              <div className="relative flex-1 py-2 w-full">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg
                    className="h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Rechercher dans les annonces..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full outline-none text-sm pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)] transition-all duration-200"
                />
              </div>

              <div className="flex gap-4 max-xl:w-full">
                <div className="relative">
                  <button
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    className="flex cursor-pointer items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-[var(--color-primary-500)] transition-all duration-200"
                  >
                    <svg
                      className="h-4 w-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                      />
                    </svg>
                    <span className="text-xs">Trier</span>
                    <svg
                      className={`h-3 w-3 text-gray-500 transition-transform ${
                        showSortDropdown ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {showSortDropdown && (
                    <div className="absolute top-full mt-2 right-0 w-48 bg-white border-2 border-gray-200 rounded-2xl shadow-xl z-10">
                      <div className="py-2 text-xs">
                        <div className="px-4 py-2 font-medium text-gray-800 border-b border-gray-100">
                          Trier par
                        </div>
                        {[
                          { value: "date_start", label: "Date de début" },
                          { value: "date_end", label: "Date de fin" },
                          { value: "created_at", label: "Date de création" },
                          { value: "content", label: "Contenu (A-Z)" },
                        ].map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setSortBy(option.value);
                              setShowSortDropdown(false);
                            }}
                            className={`w-full cursor-pointer text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                              sortBy === option.value
                                ? "bg-[var(--color-primary-50)] text-[var(--color-primary-500)] font-medium"
                                : "text-gray-700"
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex bg-gray-100 max-h-max rounded-xl ml-2 overflow-hidden">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                      viewMode === "grid"
                        ? "bg-[var(--color-primary-500)] text-white"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                      viewMode === "list"
                        ? "bg-[var(--color-primary-500)] text-white"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 10h16M4 14h16M4 18h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>


        {/* Filter Buttons */}
        <FilterButtons />

        {/* Results Count */}
        <div className="mb-8">
          <span className="inline-flex items-center px-4 py-1.5 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-xs font-medium">
            {filteredAnnonces.length} annonce{filteredAnnonces.length > 1 ? 's' : ''} trouvée{filteredAnnonces.length > 1 ? 's' : ''}
          </span>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
            <div className="flex items-center mb-3">
              <Megaphone className="h-5 w-5 text-red-500 mr-2" />
              <h3 className="text-red-800 font-semibold">Erreur de chargement</h3>
            </div>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button
              onClick={fetchAnnonces}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl text-sm font-medium"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Annonces Display */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loading />
          </div>
        ) : filteredAnnonces.length > 0 ? (
          viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredAnnonces.map((annonce) => (
                <AnnonceCard
                  key={annonce.id}
                  annonce={annonce}
                  onEdit={handleEdit}
                  fetchAnnonces={fetchAnnonces}
                  role={role}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {filteredAnnonces.map((annonce) => (
                <AnnonceList
                  key={annonce.id}
                  annonce={annonce}
                  onEdit={handleEdit}
                  fetchAnnonces={fetchAnnonces}
                  role={role}
                />
              ))}
            </div>
          )
        ) : (
          /* Empty State */
          <div className="text-center">
            <div className="w-28 h-28 mx-auto mb-6 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
              <Megaphone className="h-12 w-12 text-[var(--color-primary-500)]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {searchTerm || filterBy !== 'all' ? 'Aucune annonce trouvée' : 'Aucune annonce'}
            </h3>
            <p className="text-gray-600 text-sm mb-6">
              {searchTerm || filterBy !== 'all' 
                ? 'Essayez d\'ajuster vos filtres de recherche'
                : 'Commencez par créer votre première annonce'
              }
            </p>
            {
              (role === 1 || (role === 2 && roles.add_annonce === 1)) &&
                <Link to="/annonces/create">
                  <button className="bg-[var(--color-primary-500)] cursor-pointer text-sm hover:bg-[var(--color-primary-400)] text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300">
                    Créer une annonce
                  </button>
                </Link>
            }
          </div>
        )}
      </div>
    </div>
  );
}